import React from 'react';
import { Backdrop, Box, Typography, alpha } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Equalizer bar animation
const equalizerAnimation = keyframes`
  0%, 100% {
    height: 10px;
  }
  50% {
    height: 35px;
  }
`;

const equalizerAnimation2 = keyframes`
  0%, 100% {
    height: 35px;
  }
  50% {
    height: 10px;
  }
`;

const equalizerAnimation3 = keyframes`
  0%, 100% {
    height: 20px;
  }
  25% {
    height: 35px;
  }
  75% {
    height: 10px;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

const LoaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 20,
});

const EqualizerContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  gap: 4,
  height: 45,
  padding: '10px 20px',
  borderRadius: 12,
  background: alpha('#0F172A', 0.8),
  border: `1px solid ${alpha('#12B4A1', 0.3)}`,
  boxShadow: `0 8px 32px ${alpha('#000', 0.4)}, 0 0 20px ${alpha('#12B4A1', 0.2)}`,
});

const Bar = styled(Box)(({ delay, variant }) => ({
  width: 6,
  borderRadius: 3,
  background: 'linear-gradient(180deg, #12B4A1 0%, #0596B3 100%)',
  animation: `${variant === 1 ? equalizerAnimation : variant === 2 ? equalizerAnimation2 : equalizerAnimation3} 0.8s ease-in-out infinite`,
  animationDelay: delay,
  boxShadow: `0 0 10px ${alpha('#12B4A1', 0.5)}`,
}));

const LoadingText = styled(Typography)({
  color: '#F1F5F9',
  fontWeight: 500,
  fontSize: '0.9rem',
  letterSpacing: '0.05em',
  animation: `${pulse} 1.5s ease-in-out infinite`,
});

const Loader = ({ show, message, children, className, style }) => {
  return (
    <Box className={className} sx={{ position: 'relative', ...style }}>
      {children}
      <Backdrop
        open={show}
        sx={{
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: alpha('#0F172A', 0.85),
          backdropFilter: 'blur(8px)',
        }}
      >
        <LoaderContainer>
          <EqualizerContainer>
            <Bar delay="0s" variant={1} />
            <Bar delay="0.1s" variant={2} />
            <Bar delay="0.2s" variant={3} />
            <Bar delay="0.3s" variant={1} />
            <Bar delay="0.4s" variant={2} />
          </EqualizerContainer>
          {message && <LoadingText>{message}</LoadingText>}
        </LoaderContainer>
      </Backdrop>
    </Box>
  );
};

// Standalone loader for full page loading
export const FullPageLoader = ({ show, message = 'Loading...' }) => {
  if (!show) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: alpha('#0F172A', 0.6),
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
      }}
    >
      <LoaderContainer>
        <EqualizerContainer>
          <Bar delay="0s" variant={1} />
          <Bar delay="0.1s" variant={2} />
          <Bar delay="0.2s" variant={3} />
          <Bar delay="0.3s" variant={1} />
          <Bar delay="0.4s" variant={2} />
        </EqualizerContainer>
        <LoadingText>{message}</LoadingText>
      </LoaderContainer>
    </Box>
  );
};

// Inline loader for smaller sections
export const InlineLoader = ({ size = 'medium' }) => {
  const barWidth = size === 'small' ? 4 : size === 'large' ? 8 : 6;
  const containerHeight = size === 'small' ? 30 : size === 'large' ? 55 : 45;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: size === 'small' ? '3px' : '4px',
        height: containerHeight,
      }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          sx={{
            width: barWidth,
            borderRadius: barWidth / 2,
            background: 'linear-gradient(180deg, #12B4A1 0%, #0596B3 100%)',
            animation: `${i % 3 === 0 ? equalizerAnimation : i % 3 === 1 ? equalizerAnimation2 : equalizerAnimation3} 0.8s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </Box>
  );
};

export default Loader;
